import styled from 'styled-components'
import { useContextUI } from '../../contexts/UIContext'
import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import { Tag } from '../../constants/tags'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import CloseButton from '../CloseButton'
import { UIElement } from '../../types/UIElements'

const TagFilterWrapper = styled.div`
  opacity: 0;
  width: 100vw;
  height: 100%;
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  animation: simple-fade-in 0.5s forwards;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.8); /* Lightbox overlay style */
    z-index: 0;
    pointer-events: none;
  }

  .anchor {
    position: fixed;
    top: 50%;
  }

  &.fade-out {
    animation: simple-fade-out 0.5s forwards;
  }

  button {
    position: absolute;
    background: ${({ theme }) => theme.colors.white};
    border-radius: 50%;
    overflow: hidden;
    border: none;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden; 
    font-size: clamp(8px, calc(100vw / 125), 16px);

    animation: pulse 3s infinite;
    
    img {
      width: 115%;
      height: 115%;
      object-fit: cover;
      font-size: 14px;
      color: white;
      line-height: 8em;
      @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.lg}) {
        font-size: 18px;
      }
    }
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0,0,0,0.15);
    }
    25% {
      transform: scale(0.95);
      box-shadow: 0 0 16px 4px rgba(0,0,0,0.10);
    }
    50% {
      transform: scale(1);
      box-shadow: 0 0 16px 4px rgba(0,0,0,0.10);
    }
    75% {
      transform: scale(1.05);
      box-shadow: 0 0 16px 4px rgba(0,0,0,0.10);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0,0,0,0.15);
    }
  }

  .human {
    top: -44vh;
    left: 18vw;
    width: 14.7em;
    height: 14.7em;
  }

  .geometric {
    top: -33vh;
    left: 33vw;
    width: 17.8em;
    height: 17.8em;
  }

  .text {
    top: -44vh;
    left: 52vw;
    width: 11.4em;
    height: 11.4em;
  }

  .crest {
    top: 10vh;
    left: 66vw;
    width: 11.4em;
    height: 11.4em;
  }

  .floral {
    top: -8vh;
    left: 14vw;
    width: 20em;
    height: 20em;
  }

  .bird {
    top: 1vh;
    left: 35vw;
    width: 14.7em;
    height: 14.7em;
  }

  .animal {
    top: -20vh;
    left: 50vw;
    width: 17.8em;
    height: 17.8em;
  }

  .shape {
    top: -10vh;
    left: 76vw;
    width: 14.7em;
    height: 14.7em;
  }

  .religious {
    top: 14vh;
    left: 50vw;
    width: 13.4em;
    height: 13.4em;
  }

  .pattern {
    top: -32vh;
    left: 66vw;
    width: 14.7em;
    height: 14.7em;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
  .human {
    top: -42vh;
    left: 16vw;
    width: 12.5em;
    height: 12.5em;
  }

  .geometric {
    top: -32vh;
    left: 32vw;
    width: 15em;
    height: 15em;
  }

  .text {
    top: -42vh;
    left: 52vw;
    width: 9.5em;
    height: 9.5em;
  }

  .crest {
    top: 12vh;
    left: 70vw;
    width: 9.5em;
    height: 9.5em;
  }

  .floral {
    top: -6vh;
    left: 10vw;
    width: 17em;
    height: 17em;
  }

  .bird {
    top: 10vh;
    left: 36vw;
    width: 12.5em;
    height: 12.5em;
  }

  .animal {
    top: -18vh;
    left: 54vw;
    width: 15em;
    height: 15em;
  }

  .shape {
    top: -14vh;
    left: 80vw;
    width: 12.5em;
    height: 12.5em;
  }

  .religious {
    top: 20vh;
    left: 53vw;
    width: 11.5em;
    height: 11.5em;
  }

  .pattern {
    top: -42vh;
    left: 68vw;
    width: 12.5em;
    height: 12.5em;
  }
`

const TagFilter = () => {
  const { elementsOpen, setElementsOpen, setOverlayVisible } = useContextUI()
  const { applyTagFilter, applyGeoFilter, applyKeywordFilter } = useContextMotifDetails()
  const { i18n } = useTranslation()
  const [fadeOut, setFadeOut] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (elementsOpen.includes(UIElement.TAGFILTER)) {
      setIsOpen(true)
      applyGeoFilter(undefined)
      applyKeywordFilter(undefined)
    } else if (isOpen) {
      close()
    }
  }, [elementsOpen])

  const close = () => {
    applyTagFilter(undefined)
    setFadeOut(true)
    setOverlayVisible(false)
  }

  const handleClick = (tag: Tag | undefined) => {
    applyTagFilter(tag)
    setFadeOut(true)
    setOverlayVisible(false)
  }

  const handleCloseTagFilter = () => {
    window.history.replaceState(null, '', '/')
    setElementsOpen([UIElement.CONTROLS])
    close()
  }

  const handleAnimationEnd = () => {
    if (fadeOut) {
      setIsOpen(false)
      setFadeOut(false)
    }
  }

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const el = e.target as HTMLElement
    if (el.classList.contains('outside')) {
      handleCloseTagFilter()
    }
  }

  const filterButtons = [
    {
      name: 'human',
      action: () => handleClick(Tag.HUMAN),
    },
    {
      name: 'geometric',
      action: () => handleClick(Tag.GEOMETRIC),
    },
    {
      name: 'text',
      action: () => handleClick(Tag.TEXT),
    },
    {
      name: 'crest',
      action: () => handleClick(Tag.CREST),
    },
    {
      name: 'floral',
      action: () => handleClick(Tag.FLORAL),
    },
    {
      name: 'bird',
      action: () => handleClick(Tag.BIRD),
    },
    {
      name: 'animal',
      action: () => handleClick(Tag.ANIMAL),
    },
    {
      name: 'shape',
      action: () => handleClick(Tag.SHAPE),
    },
    {
      name: 'religious',
      action: () => handleClick(Tag.RELIGIOUS),
    },
    {
      name: 'pattern',
      action: () => handleClick(Tag.PATTERN),
    },
  ]

  return isOpen ? (
    <>
      <TagFilterWrapper
        className={`outside ${fadeOut ? 'fade-out' : ''}`}
        onClick={e => handleOutsideClick(e)}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className="anchor">
          {filterButtons.map(button => (
            <button
              key={button.name}
              className={button.name}
              onClick={button.action}
              style={{
                animationDelay: `${Math.random() * 2.2}s`,
              }}
            >
              <img
                src={`/icons/filter-buttons/${button.name}-filter-${i18n.language}.png`}
                alt={`${button.name} filter button`}
              />
            </button>
          ))}
        </div>
      </TagFilterWrapper>
      <CloseButton onClick={handleCloseTagFilter} />
    </>
  ) : null
}

export default TagFilter
